import { useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, memo, useEffect, useState } from 'react';
import AccordionFilter from './accordion-filter';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, SubjectData, SubjectsList } from 'shell/models/person';
import { DateTime } from 'luxon';
import { HeadCell } from 'shell/components/shared/table/types';
import Table from 'shell/components/shared/table';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { noop } from 'shell/utils/noop';
import { AxiosResponse } from 'axios';
import NewRegistrationPersonModal from 'shell/components/shared/registration-person-sipo-modal';
import { PersonalDataVM } from 'shell/view-models/personal-data';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { BodyVM, FilterGeneralityVM, SipoSearchFilterVM } from './view-model';

const mapSubjects = (items: SubjectData[]) => {
  return items.map((person) => {
    let birthplace = '';
    // birthplace check for object
    if (person.generalita?.luogoNascita?.localita) {
      birthplace = `${person.generalita?.luogoNascita?.localita.descrizioneLocalita} (${person.generalita?.luogoNascita?.localita.descrizioneStato})`;
    }

    if (person.generalita?.luogoNascita?.comune) {
      birthplace = `${person.generalita?.luogoNascita?.comune.nomeComune} (${person.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`;
    }

    // Residence check for object
    let residence = '';

    const residenceArray = person.residenza || [];

    // scelgo il primo o è un array?
    if (residenceArray[0]?.indirizzo) {
      residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} (${
        residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''
      })`;
    }
    // scelgo il primo o è un array?
    if (residenceArray[0]?.localitaEstera) {
      residence = `${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneLocalita} (${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneStato})`;
    }

    let state = '';
    // state check for object
    if (residenceArray[0]?.indirizzo?.toponimo) {
      const toponym = residenceArray[0]?.indirizzo?.toponimo;

      const civic = residenceArray[0]?.indirizzo?.numeroCivico;

      state = `${toponym.specie} ${toponym.denominazioneToponimo},${civic?.numero}${
        civic?.esponente1 ? `/${civic.esponente1}${civic.lettera}` : ''
      }`;
    }

    if (person.generalita?.soggettoAIRE && person.generalita?.soggettoAIRE === 'S') {
      state = 'Iscritto AIRE';
    }

    if (person.datiCancellazione && person.datiCancellazione?.descrizioneMotivoCancellazione) {
      state = `${person.datiCancellazione.descrizioneMotivoCancellazione}`;
    }

    return {
      fiscalCode: person.generalita?.codiceFiscale?.codFiscale || '',
      surname: person.generalita?.cognome || '',
      name: person.generalita?.nome || '',
      gender: person.generalita?.sesso || '',
      birthday: person.generalita?.dataNascita
        ? DateTime.fromISO(person.generalita.dataNascita).toFormat('dd/MM/yyyy')
        : '',
      birthplace,
      residence,
      state,
      idAnpr: person.generalita?.idSchedaSoggettoANPR || '',
      item: person,
      hidden: {
        delete: true,
        edit: true,
      },
    };
  });
};

export const headCells: HeadCell<BodyVM, keyof BodyVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'idAnpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: '',
  },
];

interface AccordionGeneralityProps {
  onSearchEnd?: (fields: PersonalDataVM) => void;
  redirectPath?: string;
  url?: string;
  uniquePersonUrl?: string;
  initialValues?: SipoSearchFilterVM;
}

const ResearchSipoPersonComponent = ({
  redirectPath = '/iscrizioni/aire-cittadinanza-nuova/persona-sipo',
  url = `${__PERSON_API_URL__}/non-residenti/ricerca-intestatario-cittadinanza-aire`,
  uniquePersonUrl = `${__PERSON_API_URL__}/non-residenti/soggetto`,
  onSearchEnd = noop,
  initialValues,
}: AccordionGeneralityProps): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const getPerson = useHttpClient(url, {
    options: {
      method: 'POST',
    },
  });

  const getUniquePerson = useHttpClient(uniquePersonUrl, {
    options: {
      method: 'POST',
    },
  });

  const formContext = useFormik<SipoSearchFilterVM>({
    initialValues: initialValues || {},
    onSubmit: noop,
  });

  const stateRequestApplicant = useLocation().state as {
    generality: AccordionGeneralityForm | null | undefined;
    subjectData: SubjectData;
  };

  useEffect(() => {
    if (stateRequestApplicant?.subjectData?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto) {
      formContext.setValues({
        ...(formContext.values || {}),
        fromResearch: true,
        filter: undefined,
        tablePersons: undefined,
      });
      (
        getUniquePerson({
          data: {
            matricolaSipo:
              stateRequestApplicant?.subjectData?.generalita?.idSchedaSoggettoComune
                ?.idSchedaSoggetto,
          },
        }) as ResponsePromise<SubjectsList>
      ).then((res) => {
        const castedResponse = (res as unknown as AxiosResponse<HttpResponse<SubjectsList>>)?.data
          ?.payload?.datiSoggetto;
        const [persona] = castedResponse || [];
        const generality = transformGenerality(persona);

        const freshIdAnpr = persona?.generalita?.idSchedaSoggettoANPR;
        const stateIdAnpr = stateRequestApplicant?.subjectData?.generalita?.idSchedaSoggettoANPR;
        const hasValidFreshIdAnpr = freshIdAnpr && freshIdAnpr !== '0';
        const personaWithRestoredIdAnpr =
          !hasValidFreshIdAnpr && stateIdAnpr && stateIdAnpr !== '0'
            ? {
                ...persona,
                generalita: persona?.generalita
                  ? { ...persona.generalita, idSchedaSoggettoANPR: stateIdAnpr }
                  : persona?.generalita,
              }
            : persona;

        if (generality) {
          onSearchEnd({
            ...stateRequestApplicant,
            subjectData: personaWithRestoredIdAnpr,
            generality: generality || undefined,
          });
        }
      });
    }
  }, [stateRequestApplicant?.generality]);

  const onChangeFilter = useEventCallback(
    (key: keyof FilterGeneralityVM, value: FilterGeneralityVM[keyof FilterGeneralityVM]) => {
      formContext.setFieldValue('filter', {
        ...(formContext.values?.filter || {}),
        [key]: value,
      });
    }
  );

  const onChangesFilter = useEventCallback((values: FilterGeneralityVM) => {
    formContext.setFieldValue('filter', {
      ...(formContext.values?.filter || {}),
      ...values,
    });
  });

  const [openModalNewPerson, setOpenModalNewPerson] = useState<{
    open: boolean;
    id?: string;
    idAnpr?: string;
    data?: PersonalDataVM;
    isFromTable?: boolean;
  }>({ open: false });

  const onSearch = useEventCallback(() => {
    const data = {
      criteriRicerca: {
        codiceFiscale: formContext.values?.filter?.fiscalCode || undefined,
        cognome: formContext.values?.filter?.surname || undefined,
        nome: formContext.values?.filter?.name || undefined,
        senzaCognome: formContext.values?.filter?.withoutSurname ? '1' : undefined,
        senzaNome: formContext.values?.filter?.withoutName ? '1' : undefined,
      },
      datiRichiesta: {
        ricercaNazionale: '1',
      },
    };

    (
      getPerson({ data }) as unknown as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        ResponseAnprData
      >
    ).then((response) => {
      const castedResponse = (response as unknown as AxiosResponse<HttpResponse<SubjectsList>>)
        ?.data?.payload?.datiSoggetto;
      const castedData = transformerPersonToRegisterOffice({
        data: {
          payload: {
            risposta3002OK: {
              listaSoggetti: {
                datiSoggetto: castedResponse,
              },
            },
          },
          summary: {
            code: '',
            error: false,
            messages: [],
          },
        },
      });

      const datiSoggetto = castedResponse || [];

      if (castedData?.accountholderData?.openModalNewPerson && setOpenModalNewPerson) {
        // open modal registrazione nuova persona
        setOpenModalNewPerson({
          open: true,
          data: {
            fiscalCode: formContext.values?.filter?.fiscalCode,
            name: formContext.values?.filter?.name,
            surname: formContext.values?.filter?.surname,
          },
        });
        return;
      }

      if (
        (castedData?.accountholderData?.residenceOrigin?.irreperibile ||
          ['5', '8', '9'].includes(
            datiSoggetto[0]?.datiCancellazione?.motivoCancellazione || ''
          )) &&
        setOpenModalNewPerson
      ) {
        // open modal registrazione nuova persona anche se è irreperibile
        setOpenModalNewPerson({
          open: true,
          id: castedData?.accountholderData?.idSchedaSoggetto,
          idAnpr: castedData?.accountholderData?.generality?.anprIdentifier,
          data: castedData?.accountholderData,
        });
      } else if (castedData?.accountholderData?.isSipo && setOpenModalNewPerson) {
        // open modal registrazione nuova persona
        setOpenModalNewPerson({
          open: true,
          id: castedData?.accountholderData?.idSchedaSoggetto,
          data: castedData?.accountholderData,
        });
      }

      if (datiSoggetto?.length > 1) {
        // più soggetti trovati

        formContext.setValues({
          ...(formContext.values || {}),
          filter: undefined,
          fromResearch: false,
          tablePersons: {
            body: mapSubjects(datiSoggetto),
          },
        });

        return;
      }

      const formattedGenerality = transformGenerality(datiSoggetto[0]) || {};

      // un sono soggetto trovato
      formContext.setValues({
        ...(formContext.values || {}),
        fromResearch: true,
        filter: undefined,
        tablePersons: undefined,
      });

      if (formattedGenerality) {
        onSearchEnd({ subjectData: datiSoggetto[0], generality: formattedGenerality });
      }
    });
  });

  const onChangeRowsPerPage = useEventCallback((page: number) => {
    formContext.setValues({
      ...(formContext.values || {}),
      tablePersons: {
        ...formContext.values?.tablePersons,
        page,
      },
    });
  });

  const onChangePage = useEventCallback((rowsPerPage: number) => {
    formContext.setValues({
      ...(formContext.values || {}),
      tablePersons: {
        ...formContext.values?.tablePersons,
        page: 0,
        rowsPerPage,
      },
    });
  });

  const onVisibilityPress = useEventCallback((cell: BodyVM) => {
    const castedResponse = cell.item;
    const castedData = transformerPersonToRegisterOffice({
      data: {
        payload: {
          risposta3002OK: {
            listaSoggetti: {
              datiSoggetto: [castedResponse || {}],
            },
          },
        },
        summary: {
          code: '',
          error: false,
          messages: [],
        },
      },
    })?.accountholderData;

    if (formContext.values?.tablePersons?.body?.length === 1 && castedData) {
      onSearchEnd({
        ...castedData,
        fiscalCode: castedData?.generality?.fiscalCode,
      });
      return;
    }
    if (
      (castedData?.residenceOrigin?.irreperibile ||
        ['5', '8', '9'].includes(castedResponse?.datiCancellazione?.motivoCancellazione || '')) &&
      setOpenModalNewPerson
    ) {
      // open modal registrazione nuova persona anche se è irreperibile
      setOpenModalNewPerson({
        open: true,
        id: castedData?.generality?.municipalityIdentifier,
        idAnpr: castedData?.generality?.anprIdentifier,
        data: castedData,
        isFromTable: true,
      });
      return;
    } else if (castedData?.isSipo && setOpenModalNewPerson) {
      // open modal registrazione nuova persona
      setOpenModalNewPerson({
        open: true,
        id: castedData?.generality?.municipalityIdentifier,
        data: castedData,
        isFromTable: true,
      });
      return;
    }
    if (castedData?.generality?.fiscalCode) {
      formContext.setValues({
        ...(formContext.values || {}),
        fromResearch: true,
        filter: undefined,
        tablePersons: undefined,
      });
      onSearchEnd({
        ...castedData,
        fiscalCode: castedData?.generality?.fiscalCode,
      });
    }
  });

  return (
    <Fragment>
      <NewRegistrationPersonModal
        open={openModalNewPerson.open}
        onClose={() => setOpenModalNewPerson({ open: false })}
        onNo={() => setOpenModalNewPerson({ open: false })}
        onYes={() => {
          navigate(
            {
              pathname: redirectPath,
              search: `?${queryParams.toString()}&${createSearchParams({
                idIntestatario: openModalNewPerson.data?.idSchedaSoggetto || '',
                idSchedaSoggettoANPR: openModalNewPerson.data?.generality?.anprIdentifier || '',
                codiceFiscale: openModalNewPerson.data?.fiscalCode || '',
              })}`,
            },
            {
              state: openModalNewPerson.data,
            }
          );
          setOpenModalNewPerson({ open: false });
        }}
        id={openModalNewPerson.id}
        idAnpr={openModalNewPerson.idAnpr}
        isFromTable={openModalNewPerson.isFromTable}
      />
      <AccordionFilter
        values={formContext.values.filter}
        onChange={onChangeFilter}
        onChangeFields={onChangesFilter}
        onSearch={onSearch}
      />
      {formContext.values?.tablePersons?.body ? (
        <div style={{ padding: '30px 0 30px 0' }}>
          <Table
            ariaLabel="Lista Persone"
            page={formContext.values?.tablePersons?.page || 0}
            rowsPerPage={formContext.values?.tablePersons?.rowsPerPage}
            body={(formContext.values?.tablePersons?.body || []) as Array<BodyVM>}
            count={formContext.values?.tablePersons?.body?.length || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            onVisibilityPress={onVisibilityPress}
            //hideButtons
          />
        </div>
      ) : undefined}
    </Fragment>
  );
};

ResearchSipoPersonComponent.displayName = 'Research Sipo Person component';

const ResearchSipoPerson = memo(ResearchSipoPersonComponent);

export default ResearchSipoPerson;
